// Tela de Configurações do Hub.
// Tabs por seção do catálogo HubSettings.js. Settings adminOnly só visíveis
// para usuários com setor='admin' (filtro feito no backend).

import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  FormGroup,
  Label,
  Input,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";

import { getSettings, updateSettings } from "services/settingsApi";
import { useToast } from "hooks/toast";

function fieldFor(setting, currentValue, onChange) {
  const valueAttr =
    setting.type === "password" ? "" : currentValue == null ? "" : currentValue;
  switch (setting.type) {
    case "boolean":
      return (
        <FormGroup check>
          <Label check>
            <Input
              type="checkbox"
              checked={!!currentValue}
              onChange={(e) => onChange(e.target.checked)}
            />{" "}
            <span className="form-check-sign" />
            {setting.displayName}
          </Label>
        </FormGroup>
      );
    case "number":
      return (
        <FormGroup>
          <Label>{setting.displayName}</Label>
          <Input
            type="number"
            value={valueAttr}
            onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
          />
        </FormGroup>
      );
    case "password":
      return (
        <FormGroup>
          <Label>{setting.displayName}</Label>
          <Input
            type="password"
            value={valueAttr}
            placeholder={
              setting.hasValue
                ? "•••••••• (definida — deixe em branco para manter)"
                : "Não definida"
            }
            onChange={(e) => onChange(e.target.value)}
          />
        </FormGroup>
      );
    case "text":
      // PO1-3528 (épico PO1-3527): texto longo (até 2.000, o tamanho de
      // AbpSettings.Value), ex.: "Como funciona" do leilão e do BID.
      return (
        <FormGroup>
          <Label>{setting.displayName}</Label>
          <Input
            type="textarea"
            rows={6}
            maxLength={2000}
            value={valueAttr}
            onChange={(e) => onChange(e.target.value)}
          />
        </FormGroup>
      );
    case "string":
    default:
      return (
        <FormGroup>
          <Label>{setting.displayName}</Label>
          <Input
            type="text"
            value={valueAttr}
            onChange={(e) => onChange(e.target.value)}
          />
        </FormGroup>
      );
  }
}

export default function Settings() {
  const [sections, setSections] = useState([]);
  const [settings, setSettings] = useState([]);
  const [edits, setEdits] = useState({}); // map key→novo valor
  const [activeTab, setActiveTab] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { addToast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getSettings();
      setSections(data.sections || []);
      setSettings(data.settings || []);
      setEdits({});
      if (data.sections && data.sections.length && !activeTab) {
        setActiveTab(data.sections[0].id);
      }
    } catch (err) {
      console.error("[Settings] load:", err);
      addToast({
        place: "tr",
        type: "danger",
        title: "Erro",
        description: "Erro ao carregar configurações",
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setLoading(false);
    }
  }, [addToast, activeTab]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = useCallback((key, value) => {
    setEdits((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      // Envia todas as settings visíveis com o estado atual da tela (edits
      // sobrepondo os valores que vieram do backend). Isso garante "what you
      // see is what you save" — settings que estavam marcadas pelo default
      // mas nunca persistidas viram rows no banco.
      // Sensitive (password): só envia se o usuário digitou algo (edits[key]
      // existe e não é vazio); senão omite — backend mantém o valor atual.
      const payload = {};
      for (const s of settings) {
        const edited = edits[s.key];
        if (s.sensitive) {
          if (edited !== undefined && edited !== null && edited !== '') {
            payload[s.key] = edited;
          }
          // Sensitive não digitada: omite (backend mantém o atual)
        } else {
          payload[s.key] = edited !== undefined ? edited : s.value;
        }
      }
      if (!Object.keys(payload).length) {
        addToast({
          place: 'tr',
          type: 'info',
          title: 'Nada a salvar',
          description: 'Não há configurações para enviar.',
          icon: 'tim-icons icon-bell-55',
        });
        return;
      }
      const result = await updateSettings(payload);
      addToast({
        place: "tr",
        type: "success",
        title: "Salvo",
        description: `${result.updated.length} configurações atualizadas${
          result.skipped && result.skipped.length
            ? ` (${result.skipped.length} mantidas)`
            : ""
        }.`,
        icon: "tim-icons icon-check-2",
      });
      await load();
    } catch (err) {
      console.error("[Settings] save:", err);
      const data = (err.response && err.response.data) || {};
      const message = data.error || "Erro ao salvar configurações";
      const detail =
        data.invalid || data.forbidden
          ? `: ${(data.invalid || data.forbidden).join(", ")}`
          : "";
      addToast({
        place: "tr",
        type: "danger",
        title: "Erro",
        description: `${message}${detail}`,
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setSaving(false);
    }
  }, [settings, edits, addToast, load]);

  const settingsBySection = useMemo(() => {
    const map = {};
    for (const s of settings) {
      if (!map[s.section]) map[s.section] = [];
      map[s.section].push(s);
    }
    return map;
  }, [settings]);

  // Save enabled sempre que houver settings visíveis — o handler envia o
  // estado atual da tela (com edits sobrepostos). Permite persistir defaults
  // mesmo sem o usuário ter tocado em nada (primeira vez).
  const isDirty = settings.length > 0;

  if (loading) {
    return (
      <div className="content">
        <div style={{ color: "#8fa3c8", textAlign: "center", padding: 40 }}>
          Carregando configurações…
        </div>
      </div>
    );
  }

  return (
    <div className="content">
      <Row>
        <Col md="10" className="ml-auto mr-auto">
          <h2>Configurações</h2>
          <p style={{ color: "#8fa3c8" }}>
            Exibir e alterar as configurações do aplicativo.
          </p>
        </Col>
        <Col md="2" className="text-right">
          <Button color="success" onClick={handleSave} disabled={saving || !isDirty}>
            {saving ? "Salvando…" : "Salvar"}
          </Button>
        </Col>
      </Row>
      <Row>
        <Card>
          <CardBody>
            <Nav tabs>
              {sections.map((sec) => (
                <NavItem key={sec.id}>
                  <NavLink
                    className={classnames({ active: activeTab === sec.id })}
                    onClick={() => setActiveTab(sec.id)}
                    style={{ cursor: "pointer" }}
                    title={sec.description}
                  >
                    {sec.label}
                    {sec.adminOnly && (
                      <span
                        style={{
                          fontSize: 10,
                          marginLeft: 6,
                          color: "#ffc107",
                        }}
                      >
                        admin
                      </span>
                    )}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>

            <TabContent activeTab={activeTab} style={{ padding: "20px 16px" }}>
              {sections.map((sec) => (
                <TabPane key={sec.id} tabId={sec.id}>
                  {sec.description && (
                    <p style={{ color: "#8fa3c8", fontSize: 12, marginBottom: 16 }}>
                      {sec.description}
                    </p>
                  )}
                  {(settingsBySection[sec.id] || []).map((s) => {
                    const currentValue =
                      edits[s.key] !== undefined ? edits[s.key] : s.value;
                    return (
                      <div
                        key={s.key}
                        style={{
                          marginBottom: 12,
                          paddingBottom: 12,
                          borderBottom: "1px solid #2b3553",
                        }}
                      >
                        {fieldFor(s, currentValue, (v) => handleChange(s.key, v))}
                        {s.description && s.type !== "boolean" && (
                          <div
                            style={{
                              color: "#8fa3c8",
                              fontSize: 11,
                              marginTop: 2,
                            }}
                          >
                            {s.description}
                          </div>
                        )}
                        {s.type === "boolean" && s.description && (
                          <div
                            style={{
                              color: "#8fa3c8",
                              fontSize: 11,
                              marginLeft: 22,
                              marginTop: 2,
                            }}
                          >
                            {s.description}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </TabPane>
              ))}
            </TabContent>
          </CardBody>
        </Card>
      </Row>
    </div>
  );
}
